import { Component, Input } from '@angular/core';
import { forkJoin, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ToastService } from 'src/app/common/service/toast.service';
import { StpImporterService } from 'src/app/importer-stp/service/importer-stp.service';
import { Route } from 'src/app/importer-stp/service/route.dto';
import { LineDirectionCountersResetsDto, Reset } from 'src/app/vehicles-menu/line-direction-counter-reset/line-direction-counters-reset.dto';
import { LineDirectionCountersResetsService } from 'src/app/vehicles-menu/line-direction-counter-reset/line-direction-counters-reset.service';
import { RouteQuery } from '../find-route-form/find-route-form.component';

@Component({
  selector: 'app-select-route-segment-form',
  templateUrl: './select-route-segment-form.component.html',
  styleUrls: ['./select-route-segment-form.component.scss']
})
export class SelectRouteSegmentFormComponent {
  routes: Route[] = [];
  loading: boolean = false;
  query: RouteQuery | null = null;
  existing: LineDirectionCountersResetsDto | null = null;
  selectedRoutes: FormValue[] = [];

  constructor(private importerService: StpImporterService, private resetService: LineDirectionCountersResetsService, private toast: ToastService) { }


  @Input()
  set routeQuery(q: RouteQuery | null) {
    this.selectedRoutes = [];
    this.query = q;
    this.existing = null;
    this.routes = [];
    if (q != null) {
      this.loading = true;
      forkJoin({
        routes: this.importerService.getRoutes(q.carrierId, q.line, q.direction),
        existing: this.resetService.getByCarrierLineDir(q.carrierId, q.line, q.direction).pipe(
          catchError(err => err.status === 404 ? of(null) : throwError(() => err))
        )
      }).subscribe({
        next: ({ routes, existing }) => {
          if (existing != null) {
            this.existing = existing;
            this.selectedRoutes = existing.resets.map(r => new FormValue(r.sequenceNumber, r.stopCode));
          }
          this.loading = false;
          this.routes = [...routes];
        },
        error: error => {
          this.loading = false;
          console.error(error);
        }
      });
    }
  }

  handleFormClear(): void {
    this.selectedRoutes = [];
    if (this.existing != null) {
      this.resetService.delete(this.existing.getVersionedId())
        .subscribe(() => {
          this.toast.showWarnToast(
            'line-direction-counters-resets.variant',
            'line-direction-counters-resets.reset-stop-removed',
            { line: this.existing?.line, direction: this.existing?.direction }
          );
          this.existing = null;
        }, error => {
          console.error(error);
          this.toast.showErrorToast('line-direction-counters-resets.delete-server-error');
        });
    }
  }

  handleFormSubmmited(): void {
    if (this.selectedRoutes.length > 0) {
      const cmd = {
        carrier: this.query?.carrierId,
        line: this.query?.line,
        direction: this.query?.direction,
        resets: this.selectedRoutes.map(s => new Reset(s.stopCode, s.sequenceNumber))
      };
      (this.existing != null ? this.resetService.update(this.existing.getVersionedId(), cmd) : this.resetService.save(cmd))
        .pipe(
          switchMap(id => this.resetService.getById(id.id))
        )
        .subscribe(v => {
          this.toast.showInfoToast('line-direction-counters-resets.reset-stop-added');
          this.existing = v;
        }, error => {
          console.error(error);
          this.toast.showErrorToast('line-direction-counters-resets.save-server-error');
        });
    }
  }

  get hasChanged(): boolean {
    if (this.existing != null) {
      if (this.existing.resets.length !== this.selectedRoutes.length)
        return true;
      const now = this.existing?.resets?.map(r => new FormValue(r.sequenceNumber, r.stopCode)) ?? [];
      const createKey = (item: any): string => `${item.sq}_${item.stopCode}`;
      const nowKeys = now.map(createKey);
      const selectedKeys = this.selectedRoutes.map(createKey);
      return !nowKeys.every(key => selectedKeys.includes(key));
    } else {
      return this.selectedRoutes.length > 0;
    }
  }

  toFormValue(route: Route): FormValue {
    return new FormValue(route?.sequenceNumber, route?.routeSegment?.stopPileBegin?.code);
  }

}

class FormValue {
  constructor(public readonly sequenceNumber: number, public readonly stopCode: string) { }
}
